import { SafetyCertificateOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Space, message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import {
  socialAccountLoginCancel,
  socialAccountLoginFinish,
  socialAccountLoginQrcode,
  socialAccountLoginSms,
  socialAccountLoginStart,
  socialAccountLoginStatus,
} from '../api';
import { resolveErrorMessage } from '../utils/errorMessage';

const SUPPORTED = new Set(['douyin', 'kuaishou', 'weixin_channels']);
const AUTO_START_GUARD_TTL_MS = 120000;
const autoStartGuardMap = new Map();

const normalizePlatformCode = (account) => String(account?.platform_code || '').trim();
const buildAutoStartGuardKey = (platformCode, accountId, autoStartToken) => (
  `${platformCode || 'unknown'}::${accountId || 'unknown'}::${String(autoStartToken || 'default')}`
);

const shouldSkipAutoStart = (guardKey) => {
  const now = Date.now();
  for (const [key, ts] of autoStartGuardMap.entries()) {
    if (!ts || now - ts > AUTO_START_GUARD_TTL_MS) autoStartGuardMap.delete(key);
  }
  if (autoStartGuardMap.has(guardKey)) return true;
  autoStartGuardMap.set(guardKey, now);
  return false;
};

const SocialAccountLoginButton = ({
  account,
  size = 'small',
  type = 'link',
  label = '扫码登录',
  disabled = false,
  autoStart = false,
  autoStartToken = 'default',
  onSuccess,
  buttonProps = {},
}) => {
  const platformCode = normalizePlatformCode(account);
  const accountId = account?.account_id;
  const accountName = account?.account_name || accountId;

  const [starting, setStarting] = useState(false);
  const [open, setOpen] = useState(false);
  const [qrImg, setQrImg] = useState('');
  const [statusText, setStatusText] = useState('');
  const [finishing, setFinishing] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [smsRequired, setSmsRequired] = useState(false);
  const [smsPhoneMask, setSmsPhoneMask] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsSubmitting, setSmsSubmitting] = useState(false);
  const [pendingConfirmReason, setPendingConfirmReason] = useState('');

  const pollRef = useRef(null);
  const autoStartedRef = useRef(false);

  const cleanupPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const closeModal = async () => {
    cleanupPolling();
    setOpen(false);
    setQrImg('');
    setStatusText('');
    setFinishing(false);
    setSmsRequired(false);
    setSmsPhoneMask('');
    setSmsCode('');
    setPendingConfirmReason('');
    if (sessionId) {
      try {
        await socialAccountLoginCancel({ session_id: sessionId });
      } catch (_) {
        // ignore
      }
    }
    setSessionId('');
  };

  useEffect(() => () => cleanupPolling(), []);

  useEffect(() => {
    if (!autoStart) return;
    if (!accountId || !SUPPORTED.has(platformCode)) return;
    if (autoStartedRef.current) return;
    const guardKey = buildAutoStartGuardKey(platformCode, accountId, autoStartToken);
    if (shouldSkipAutoStart(guardKey)) return;
    autoStartedRef.current = true;
    Promise.resolve()
      .then(() => handleStart())
      .catch(() => {
        // ignore
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, autoStartToken, platformCode, accountId]);

  if (!SUPPORTED.has(platformCode)) return null;

  const refreshQr = async (sidOverride) => {
    const isEventLike = sidOverride && typeof sidOverride === 'object' && 'nativeEvent' in sidOverride;
    const sid = (!isEventLike && typeof sidOverride === 'string' && sidOverride) ? sidOverride : sessionId;
    if (!sid) return;
    try {
      setStatusText('正在刷新二维码...');
      const resp = await socialAccountLoginQrcode({ session_id: sid });
      if (resp?.status === 'success' && resp?.qrcode_img) {
        setQrImg(String(resp.qrcode_img));
        setStatusText('请使用手机扫码登录...');
      } else {
        setQrImg('');
        message.error(resolveErrorMessage(resp?.error_message, '刷新二维码失败'));
      }
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '刷新二维码失败'));
    }
  };

  const finishIfLoggedIn = async (sidOverride) => {
    const sid = sidOverride || sessionId;
    if (!sid) return;
    setFinishing(true);
    try {
      setStatusText('登录成功，正在保存 cookies 并同步到 OSS/ES...');
      const resp = await socialAccountLoginFinish({ session_id: sid });
      if (resp?.status === 'success') {
        message.success('登录成功，cookies 已写回配置');
        await closeModal();
        if (typeof onSuccess === 'function') onSuccess(resp);
      } else {
        message.error(resolveErrorMessage(resp?.error_message, '保存 cookies 失败'));
      }
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '保存 cookies 失败'));
    } finally {
      setFinishing(false);
    }
  };

  const startPolling = (sid) => {
    cleanupPolling();
    if (!sid) return;
    pollRef.current = setInterval(async () => {
      try {
        const s = await socialAccountLoginStatus({ session_id: sid });
        if (s?.status !== 'success') return;
        const st = String(s?.login_status || '').trim();
        if (st === 'logged_in') {
          cleanupPolling();
          setPendingConfirmReason('');
          await finishIfLoggedIn(sid);
          return;
        }
        if (st === 'sms_required') {
          cleanupPolling();
          setPendingConfirmReason('');
          setSmsRequired(true);
          setQrImg('');
          setSmsPhoneMask(String(s?.sms_phone_mask || ''));
          setStatusText('需要短信验证码，请在下方输入后继续');
          return;
        }
        if (st === 'blocked') {
          cleanupPolling();
          setPendingConfirmReason('');
          setStatusText(`登录被平台拦截（${s?.blocked_reason || '风控/验证码'}），请稍后重试或人工处理`);
          return;
        }
        if (st === 'failed') {
          cleanupPolling();
          setPendingConfirmReason('');
          const reason = resolveErrorMessage(s?.error, '未知原因');
          setStatusText(`检测失败：${reason}`);
          return;
        }
        if (st === 'pending_confirm') {
          const timeoutReached = Boolean(s?.pending_confirm_timeout_reached);
          const timeoutReason = String(s?.pending_confirm_timeout_reason || '').trim();
          if (timeoutReached) {
            setPendingConfirmReason(timeoutReason || '已扫码但长时间未完成登录，请在手机微信完成确认后重试。');
          } else {
            setPendingConfirmReason('');
          }
          setStatusText(String(s?.login_hint || '已扫码，请在手机上确认登录...'));
          return;
        }
        setPendingConfirmReason('');
        const hint = String(s?.login_hint || '').trim();
        setStatusText(hint || '请使用手机扫码登录...');
      } catch (_) {
        // ignore transient
      }
    }, 2500);
  };

  const submitSms = async () => {
    if (!sessionId) return;
    const code = String(smsCode || '').trim();
    if (!code) {
      message.warning('请输入短信验证码');
      return;
    }
    setSmsSubmitting(true);
    try {
      const resp = await socialAccountLoginSms({ session_id: sessionId, sms_code: code });
      if (resp?.status !== 'success') {
        message.error(resolveErrorMessage(resp?.error_message, '验证码提交失败'));
        return;
      }
      setSmsRequired(false);
      setSmsPhoneMask('');
      setSmsCode('');
      setStatusText('验证码已提交，正在验证登录状态...');
      startPolling(sessionId);
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '验证码提交失败'));
    } finally {
      setSmsSubmitting(false);
    }
  };

  const handleStart = async () => {
    if (!accountId) {
      message.error('缺少 account_id');
      return;
    }
    setStarting(true);
    try {
      const resp = await socialAccountLoginStart({ account_id: accountId });
      if (resp?.status !== 'success') {
        message.error(resolveErrorMessage(resp?.error_message, '启动扫码登录失败'));
        return;
      }
      const sid = String(resp?.session_id || '');
      if (!sid) {
        message.error('启动扫码登录失败：缺少 session_id');
        return;
      }
      setSessionId(sid);
      const img = String(resp?.qrcode_img || '');
      if (img) setQrImg(img);
      setOpen(true);
      setStatusText('请使用手机扫码登录...');
      setPendingConfirmReason('');
      startPolling(sid);
      if (!img) {
        setTimeout(() => {
          refreshQr(sid);
        }, 1200);
      }
    } catch (e) {
      message.error(resolveErrorMessage(e?.message || String(e), '启动扫码登录失败'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <Button
        type={type}
        size={size}
        icon={<SafetyCertificateOutlined />}
        onClick={handleStart}
        loading={starting}
        disabled={disabled || starting || finishing}
        {...buttonProps}
      >
        {label}
      </Button>

      <Modal
        title={`扫码登录：${accountName || '账号'}（${platformCode}）`}
        open={open}
        onCancel={closeModal}
        footer={(
          <Space>
            <Button onClick={closeModal}>
              关闭
            </Button>
          </Space>
        )}
      >
        {smsRequired ? (
          <div style={{
            minHeight: 220,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
          >
            <div style={{ color: '#333', marginBottom: 8 }}>
              {statusText || '需要短信验证码，请在下方输入后继续'}
            </div>
            {smsPhoneMask && (
              <div style={{ color: '#666', marginBottom: 12 }}>验证码手机号：{smsPhoneMask}</div>
            )}
            <div style={{ display: 'flex', gap: 8, width: '100%', maxWidth: 360 }}>
              <input
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value)}
                placeholder="请输入短信验证码"
                style={{
                  flex: 1,
                  height: 32,
                  padding: '0 8px',
                  borderRadius: 6,
                  border: '1px solid #d9d9d9',
                }}
              />
              <Button type="primary" onClick={submitSms} loading={smsSubmitting}>
                提交
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{
              width: 260,
              height: 260,
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fff',
            }}
            >
              {qrImg ? (
                <img
                  src={qrImg}
                  alt="social_login_qrcode"
                  style={{
                    width: '100%',
                    height: '100%',
                    maxWidth: 240,
                    maxHeight: 240,
                    objectFit: 'contain',
                    objectPosition: 'center',
                  }}
                />
              ) : (
                <div style={{ color: '#999' }}>二维码加载中...</div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#333', marginBottom: 8 }}>扫码后会自动检测登录状态，并同步 cookies。</div>
              <div style={{ color: '#999' }}>{statusText || '-'}</div>
              {pendingConfirmReason && (
                <div style={{ marginTop: 10 }}>
                  <Alert
                    showIcon
                    type="warning"
                    message="手机确认超时"
                    description={pendingConfirmReason}
                  />
                </div>
              )}
              {finishing && <div style={{ color: '#999', marginTop: 8 }}>正在上传 OSS 并回写配置...</div>}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default SocialAccountLoginButton;
